'use client';

import {
  FloatingFocusManager,
  type FloatingFocusManagerProps,
  useMergeRefs,
} from '@floating-ui/react';
import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { useSlotContent } from '../../hooks/use-slot-content';
import { Slot } from '../slot';
import { PopoverRootContext } from './context/popover-root.context';
import {
  popoverContentPositionerRecipe,
  popoverContentRecipe,
} from './styles/popover-content.styles';
import type { PopoverContentPropsInterface } from './types/popover-content.types';

/**
 * @since 10.0.0
 */
export const PopoverContent = forwardRef<HTMLDivElement, PopoverContentPropsInterface>(
  (props: PopoverContentPropsInterface, forwardedRef) => {
    const { popover, modal, dividers, tone } = useContext(PopoverRootContext);
    const {
      asChild = false,
      children,
      className,
      css: cssProp,
      focusManagerProps: userFocusManagerProps = {},
      style = {},
      ...rest
    } = props;

    const contentPositionerStyles = css(
      popoverContentPositionerRecipe.raw({ modal, dividers, tone }),
    );
    const contentStyles = css(popoverContentRecipe.raw({ modal }), cssProp);

    const focusManagerProps: Partial<FloatingFocusManagerProps> = {
      order: ['floating', 'content'],
      modal: true,
      ...userFocusManagerProps,
    };

    const mergedRefs = useMergeRefs([popover.refs.setFloating, forwardedRef]);

    const Component = asChild ? Slot : 'div';

    const content = useSlotContent({
      children,
      asChild,
      template: (childContent) => (
        <div
          data-c11n-version={__C11N_VERSION__}
          data-c11n-component="Popover.Content"
          className={cx('c11n', contentStyles, className)}
          style={{ ...popover.floatingTransitionStyles, ...style }}
        >
          {childContent}
        </div>
      ),
      dependencies: [contentStyles, className, popover.floatingTransitionStyles, style],
    });

    return (
      <FloatingFocusManager context={popover.context} {...focusManagerProps}>
        <Component
          ref={mergedRefs}
          aria-labelledby={popover.labelId}
          aria-describedby={popover.descriptionId}
          data-c11n-version={__C11N_VERSION__}
          data-c11n-component="Popover.ContentPositioner"
          className={cx('c11n', contentPositionerStyles)}
          style={
            modal
              ? {}
              : {
                  ...popover.floatingStyles,
                  visibility: popover.middlewareData.hide?.referenceHidden ? 'hidden' : 'visible',
                }
          }
          {...popover.getFloatingProps(rest)}
        >
          {content}
        </Component>
      </FloatingFocusManager>
    );
  },
);

PopoverContent.displayName = 'Popover.Content';
